import React, { useState } from 'react';
import { FileText, Download, Calendar, X, Lock } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { usePlan } from '../context/PlanContext';
import { calcSaleCommission } from '../utils/commissionCalc';
import { getHistoryFilter } from '../utils/historyFilter';
import * as XLSX from 'xlsx';

export default function Earnings() {
  const {
    userData, sales, leads, activities, bonusTiers,
    unitsSold, newSales, usedSales,
    allFrontGross, allBackGross, totalSpiffs,
    totalGross, commission, unitBonuses,
    currentTier, tierBonus, totalEarnings,
    collapsedSections, toggleSection,
  } = useAppContext();

  const { planConfig, isPro, isFeatureEnabled } = usePlan();
  const canExcel = isFeatureEnabled('exportExcel');
  // Apply history filter to visible sales list
  const filteredSales = sales.filter(getHistoryFilter(planConfig));

  // Export state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportType, setExportType] = useState('pdf');
  const [exportRange, setExportRange] = useState('month');
  const [exportStart, setExportStart] = useState('');
  const [exportEnd, setExportEnd] = useState('');

  const getFilteredSales = () => {
    if (exportRange === 'month') {
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
      return sales.filter(s => {
        const d = new Date(s.soldDate);
        return d >= monthStart && d <= monthEnd;
      });
    } else {
      const start = exportStart ? new Date(exportStart + 'T00:00:00') : new Date(0);
      const end = exportEnd ? new Date(exportEnd + 'T23:59:59') : new Date();
      return sales.filter(s => {
        const d = new Date(s.soldDate);
        return d >= start && d <= end;
      });
    }
  };

  const getRangeLabel = () => {
    if (exportRange === 'month') {
      return new Date().toLocaleString('default', { month: 'long', year: 'numeric' });
    }
    const s = exportStart ? new Date(exportStart).toLocaleDateString() : 'Start';
    const e = exportEnd ? new Date(exportEnd).toLocaleDateString() : 'Now';
    return `${s} – ${e}`;
  };

  // ── EXPORT: PDF ──────────────────────────────────────────────────────────
  const exportPDF = () => {
    const filtered = getFilteredSales();
    const rangeLabel = getRangeLabel();
    const now = new Date();
    
    const fUnits = filtered.length;
    const fSpiffs = filtered.reduce((t, s) => t + (Number(s.spiffs) || 0), 0);
    const fComm = filtered.reduce((t, s) => t + calcSaleCommission(s, userData), 0);
    const fUB = filtered.reduce((t, s) => t + (s.condition === 'New' ? (Number(userData.newUnitBonus) || 0) : (Number(userData.usedUnitBonus) || 0)), 0);
    const fTier = (() => { const matched = [...bonusTiers].sort((a,b) => b.unitsSold - a.unitsSold).find(t => fUnits >= t.unitsSold); return matched ? matched.bonusAmount : 0; })();
    const fTotal = Math.round(fComm) + fSpiffs + fUB + fTier;
    
    let html = `
      <html><head><style>
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 30px; color: #1a1a1a; }
        h1 { color: #00cc77; margin-bottom: 4px; }
        h2 { color: #333; border-bottom: 2px solid #00cc77; padding-bottom: 6px; margin-top: 24px; }
        .summary { display: flex; gap: 20px; flex-wrap: wrap; margin: 16px 0; }
        .summary-card { background: #f5f5f5; padding: 14px 20px; border-radius: 10px; min-width: 140px; }
        .summary-card .label { font-size: 12px; color: #666; text-transform: uppercase; }
        .summary-card .value { font-size: 22px; font-weight: 700; color: #1a1a1a; }
        table { width: 100%; border-collapse: collapse; margin-top: 12px; }
        th { background: #00cc77; color: white; padding: 10px; text-align: left; font-size: 13px; }
        td { padding: 9px 10px; border-bottom: 1px solid #eee; font-size: 13px; }
        tr:nth-child(even) { background: #f9f9f9; }
        .footer { margin-top: 30px; color: #999; font-size: 11px; text-align: center; }
        @media print { body { padding: 0; } }
      </style></head><body>
      <h1>TRACKSY</h1>
      <p style="color:#666;margin-top:0;">Sales Report — ${rangeLabel}</p>
      <p style="color:#444;">${userData.name || 'Salesperson'} ${userData.dealership ? '· ' + userData.dealership : ''}</p>
      
      <div class="summary">
        <div class="summary-card"><div class="label">Units Sold</div><div class="value">${fUnits}</div></div>
        <div class="summary-card"><div class="label">Total Earnings</div><div class="value">$${fTotal.toLocaleString()}</div></div>
        <div class="summary-card"><div class="label">Commission</div><div class="value">$${Math.round(fComm).toLocaleString()}</div></div>
        <div class="summary-card"><div class="label">Spiffs</div><div class="value">$${fSpiffs.toLocaleString()}</div></div>
        <div class="summary-card"><div class="label">Tier Bonus</div><div class="value">$${fTier.toLocaleString()}</div></div>
      </div>
      
      <h2>Sale Details</h2>
      <table>
        <tr><th>#</th><th>Customer</th><th>Vehicle</th><th>Type</th><th>Front $</th><th>Back $</th><th>Spiffs</th><th>Commission</th><th>Date</th></tr>
        ${filtered.map((s, i) => {
          const comm = calcSaleCommission(s, userData);
          const ub = s.condition === 'New' ? userData.newUnitBonus : userData.usedUnitBonus;
          const date = new Date(s.soldDate).toLocaleDateString();
          return `<tr><td>${i+1}</td><td>${s.customerName}</td><td>${s.vehicle}</td><td>${s.condition}</td><td>$${s.frontGross.toLocaleString()}</td><td>$${s.backGross.toLocaleString()}</td><td>$${s.spiffs.toLocaleString()}</td><td>$${Math.round(comm + ub).toLocaleString()}</td><td>${date}</td></tr>`;
        }).join('')}
      </table>
      
      <div class="footer">Generated by Tracksy · ${now.toLocaleDateString()} ${now.toLocaleTimeString()}</div>
      </body></html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => { printWindow.print(); };
    setShowExportModal(false);
  };

  // ── EXPORT: Excel (.xlsx, Pro only) ─────────────────────────────────────
  const exportExcel = () => {
    if (!canExcel) return;
    const filtered = getFilteredSales();
    const now = new Date();

    // Sheet 1: Sales
    const salesRows = filtered.map((s, i) => ({
      '#': i + 1,
      Date: new Date(s.soldDate).toLocaleDateString(),
      Customer: s.customerName,
      Vehicle: s.vehicle,
      Condition: s.condition,
      'Front Gross': s.frontGross,
      'Back Gross': s.backGross,
      Spiffs: s.spiffs || 0,
      Commission: Math.round(calcSaleCommission(s, userData)),
    }));

    // Sheet 2: Leads
    const leadsRows = (leads || []).map(l => ({
      Name: l.customerName,
      Temperature: l.temperature,
      Status: l.status || '',
      'Vehicle of Interest': l.vehicleOfInterest || '',
      Phone: l.phone || '',
      Notes: l.notes || '',
    }));

    // Sheet 3: Activity
    const actRows = (activities || []).map(a => ({
      Date: new Date(a.date).toLocaleDateString(),
      Type: a.type,
      Customer: a.customerName || '',
    }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(salesRows), 'Sales');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(leadsRows.length ? leadsRows : [{ Note: 'No leads' }]), 'Leads');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(actRows.length ? actRows : [{ Note: 'No activity' }]), 'Activity');

    XLSX.writeFile(wb, `Tracksy_${now.getFullYear()}_${String(now.getMonth()+1).padStart(2,'0')}.xlsx`);
    setShowExportModal(false);
  };

  const isSplit = userData.commissionMode === 'split';
  const commSub = isSplit
    ? `(${userData.frontPct}% × $${allFrontGross.toLocaleString()} front) + (${userData.backPct}% × $${allBackGross.toLocaleString()} back)`
    : `${userData.bothPct}% × $${totalGross.toLocaleString()} (front + back)`;

  return (
    <div className="view">
      <div className="earn-hero">
        <div className="earn-total-lbl">Total Estimated Earnings</div>
        <div className="earn-total">${totalEarnings.toLocaleString()}</div>
      </div>
      <div className="earn-grid earn-grid-3">
        <div className="earn-card">
          <div className="earn-card-lbl">Commission</div>
          <div className="earn-card-val">${Math.round(commission).toLocaleString()}</div>
          <div className="earn-card-sub">{commSub}</div>
        </div>
        <div className="earn-card">
          <div className="earn-card-lbl">Unit Bonuses</div>
          <div className="earn-card-val">${unitBonuses.toLocaleString()}</div>
          <div className="earn-card-sub">${userData.newUnitBonus} × {newSales.length} units</div>
        </div>
        <div className="earn-card">
          <div className="earn-card-lbl">Tier Bonus</div>
          <div className="earn-card-val">${tierBonus.toLocaleString()}</div>
          <div className="earn-card-sub">{currentTier ? `${currentTier.unitsSold}-unit tier` : 'Not achieved yet'}</div>
        </div>
      </div>
      {totalSpiffs > 0 && (
        <div className="spiff-banner">
          <span className="spiff-label">⚡ Spiffs</span>
          <span className="spiff-val">+${totalSpiffs.toLocaleString()}</span>
          <span className="spiff-sub">Added directly to total earnings</span>
        </div>
      )}
      <div className="section-card">
        <div className="section-hdr section-hdr-toggle" onClick={() => toggleSection('earnings-breakdown')}>
          <h2>Sale Breakdown</h2>
          <span className={`section-chevron ${collapsedSections['earnings-breakdown'] ? 'chevron-collapsed' : ''}`}>▼</span>
        </div>
        <div className={`section-collapse-content ${collapsedSections['earnings-breakdown'] ? 'section-collapsed' : ''}`}>
        {!isPro && (
          <div style={{fontSize:12,color:'rgba(255,255,255,.35)',marginBottom:8,padding:'6px 0'}}>
            Showing last 2 months. <span style={{color:'rgba(0,255,153,.6)'}}>Upgrade to Pro for full history.</span>
          </div>
        )}
        {filteredSales.length === 0 && <div className="empty-state">No sales yet.</div>}
        {filteredSales.map(sale => {
          const comm = calcSaleCommission(sale, userData);
          const ub   = sale.condition === 'New' ? userData.newUnitBonus : userData.usedUnitBonus;
          const frontComm = sale.frontGross * (userData.commissionMode === 'combined'
            ? userData.bothPct / 100 : userData.frontPct / 100);
          const backComm  = sale.backGross  * (userData.commissionMode === 'combined'
            ? userData.bothPct / 100 : userData.backPct  / 100);
          return (
            <div key={sale.id} className="sale-detail-row">
              <div className="sdl-top">
                <div>
                  <div className="sdl-name">{sale.customerName}</div>
                  <div className="sdl-vehicle">{sale.vehicle} · <span className={`cond-badge cond-${sale.condition.toLowerCase()}`}>{sale.condition}</span></div>
                </div>
                <div className="sdl-earn">${Math.round(comm + ub + sale.spiffs).toLocaleString()}</div>
              </div>
              <div className="sdl-breakdown">
                <span className="sdl-item">Front: <strong>${sale.frontGross.toLocaleString()}</strong> → <span className="sdl-comm">${Math.round(frontComm).toLocaleString()}</span></span>
                <span className="sdl-item">Back: <strong>${sale.backGross.toLocaleString()}</strong> → <span className="sdl-comm">${Math.round(backComm).toLocaleString()}</span></span>
                {sale.spiffs > 0 && <span className="sdl-item sdl-spiff">Spiff: +${sale.spiffs.toLocaleString()}</span>}
                {ub > 0         && <span className="sdl-item sdl-ub">Bonus: +${ub}</span>}
              </div>
            </div>
          );
        })}
        </div>{/* end section-collapse-content */}
      </div>
      {/* Export Buttons */}
      <div className="export-section">
        <button className="export-btn export-pdf" onClick={() => { setExportType('pdf'); setShowExportModal(true); }}>
          <FileText size={18} /> Export PDF
        </button>
        {canExcel ? (
          <button className="export-btn export-excel" onClick={() => { setExportType('excel'); setShowExportModal(true); }}>
            <Download size={18} /> Export Excel
          </button>
        ) : (
          <button className="export-btn export-excel" style={{opacity:.45,cursor:'default'}} title="Available on Tracksy Pro ↑">
            <Lock size={16} /> Excel (Pro)
          </button>
        )}
      </div>
      {showExportModal && (
        <div className="modal-overlay" onClick={() => setShowExportModal(false)}>
          <div className="modal-sheet export-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-hdr">
              <h3>{exportType === 'pdf' ? 'Export PDF' : 'Export Excel'}</h3>
              <button className="modal-x" onClick={() => setShowExportModal(false)}><X size={20} /></button>
            </div>
            <div className="export-range-options">
              <button className={`export-range-btn ${exportRange === 'month' ? 'active' : ''}`} onClick={() => setExportRange('month')}>
                <Calendar size={16} /> This Month
              </button>
              <button className={`export-range-btn ${exportRange === 'custom' ? 'active' : ''}`} onClick={() => setExportRange('custom')}>
                <Calendar size={16} /> Custom Range
              </button>
            </div>
            {exportRange === 'custom' && (
              <div className="export-dates">
                <div className="export-date-field">
                  <label>Start Date</label>
                  <input type="date" value={exportStart} onChange={e => setExportStart(e.target.value)} />
                </div>
                <div className="export-date-field">
                  <label>End Date</label>
                  <input type="date" value={exportEnd} onChange={e => setExportEnd(e.target.value)} />
                </div>
              </div>
            )}
            <div className="export-preview">
              {getFilteredSales().length} sales found for {getRangeLabel()}
            </div>
            <button className={`export-go-btn ${exportType === 'pdf' ? 'export-pdf' : 'export-excel'}`}
              onClick={() => exportType === 'pdf' ? exportPDF() : exportExcel()}>
              {exportType === 'pdf' ? <><FileText size={18} /> Generate PDF</> : <><Download size={18} /> Download Excel</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
